import { Box, TextField } from '@mui/material';
import { DataGrid, GridPaginationModel } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import { ICommonDatagridProps } from '../../types/commonDatagrid';

export const CommonDatagrid = ({
  header = null,
  data,
  columns,
  rowId,
  headerActionButtons = <></>,
  searchbarPlaceholder = null,
  searchKey = '',
  serverPagination = false,
  page = 0,
  pageSize = 10,
  rowCount = 0,
  loading = false,
  onPageChange,
  onPageSizeChange,
  onChangeSearchKeyword: onChangeSearchkey,
  isCellEditable = false,
  onRowClick,
}: ICommonDatagridProps) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  /**
   * Debounce search input
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  /**
   * Client side filtering
   */
  const filteredRows = useMemo(() => {
    if (serverPagination) return data;

    if (!debouncedSearch || !searchKey) return data;

    return data.filter((row: any) =>
      row[searchKey]?.toLowerCase().includes(debouncedSearch.toLowerCase()),
    );
  }, [data, debouncedSearch, searchKey, serverPagination]);

  /**
   * Trigger server search (debounced)
   */
  useEffect(() => {
    if (serverPagination && onChangeSearchkey) {
      onChangeSearchkey(debouncedSearch);
    }
  }, [debouncedSearch, serverPagination]);

  const paginationModel: GridPaginationModel = {
    page,
    pageSize,
  };

  const rowsToRender = serverPagination ? data : filteredRows;

  return (
    <>
      {(header || searchbarPlaceholder) && (
        <Box mb={2} display="flex" flexDirection="column" gap={2}>
          {header}

          {searchbarPlaceholder && (
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <TextField
                size="small"
                placeholder={searchbarPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 200,
                  '& .MuiInputBase-input': {
                    fontSize: 12,
                    height: 20,
                  },
                }}
              />
            </Box>
          )}
        </Box>
      )}

      <Box mb={2}>{headerActionButtons}</Box>

      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
        }}
      >
        <Box
          sx={{
            width: columns.length * 150,
            minWidth: '100%',
          }}
        >
          <DataGrid
            getRowId={(row) => row[rowId]}
            rows={rowsToRender}
            columns={columns}
            autoHeight
            loading={loading}
            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            onRowClick={onRowClick}
            {...(serverPagination && {
              paginationMode: 'server',
              rowCount,
              paginationModel,
              onPaginationModelChange: (model) => {
                if (model.pageSize !== pageSize) {
                  onPageSizeChange?.(model.pageSize);
                } else {
                  onPageChange?.(model.page);
                }
              },
            })}
            sx={{
              ...(onRowClick && { '& .MuiDataGrid-row': { cursor: 'pointer' } }),
              '& .MuiDataGrid-cell': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: '1.4',
                alignItems: 'flex-start',
                paddingTop: '8px',
                paddingBottom: '8px',
              },
              '& .MuiDataGrid-row:nth-of-type(odd)': {
                backgroundColor: '#f9f9f9',
              },
              '& .MuiDataGrid-row:nth-of-type(even)': {
                backgroundColor: '#fff',
              },
              '& .MuiDataGrid-cell:focus-within': {
                outline: 'none',
              },
              '& .MuiDataGrid-cell.MuiDataGrid-editCell': {
                outline: 'none',
              },
              '& .MuiDataGrid-cell.MuiDataGrid-cell--editable:focus-within': {
                outline: 'none',
              },
              minWidth: '100%',
            }}
            getRowHeight={() => 'auto'}
            isCellEditable={
              typeof isCellEditable === 'function' ? isCellEditable : () => !!isCellEditable // fallback to boolean
            }
          />
        </Box>
      </Box>
    </>
  );
};
